<template>
  <div
    class="login-view"
    @keyup.enter="onEnterPressed"
  >
    <div class="floating-card">
      <h1 class="card-text">
        Log In
      </h1>
      <TextInput
        v-model="email"
        label="Email"
        :required="true"
      />
      <TextInput
        v-model="password"
        label="Password"
        type="password"
        :required="true"
      />
      <div
        v-if="showEmailConfirmationNeeded"
        class="error-text"
      >
        Email confirmation is required prior to login.
        <router-link :to="'/send-email-confirmation?email=' + email">
          Send Email Confirmation
        </router-link>
      </div>
      <div
        v-show="showErrorMessage"
        class="error-text"
      >
        {{ errorMessage }}
      </div>
      <div class="button-wrapper">
        <LockingButton
          class="locking-button"
          :disabled="!formValid"
          :locked="loading"
          :text="'Log In'"
          @click="login"
        />
      </div>
      <div class="router-links">
        <router-link :to="'/forgot-password?email=' + email">
          Forgot Password?
        </router-link>
        <span class="divider">|</span>
        <router-link to="/create-account">
          Create Account
        </router-link>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from 'vue'
import TextInput from '@/components/shared/TextInput.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import router from '@/router'
import { MiscellaneousService } from '@/services/miscellaneous.service'
import { LoginMessage } from '@/@types/messages/LoginMessage'
import LockingButton from '@/components/shared/LockingButton.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'Login',
  components: {
    TextInput,
    LockingButton
  },
  props: {
    returnUrl: {
      type: String,
      default: ''
    }
  },
  setup () {
    const miscellaneousService = inject(PROVIDER_CONSTANTS.miscellaneousService) as MiscellaneousService
    return {
      miscellaneousService
    }
  },
  data () {
    return {
      email: '',
      password: '',
      loading: false,
      showErrorMessage: false,
      errorMessage: '',
      showEmailConfirmationNeeded: false
    }
  },
  computed: {
    formValid (): boolean {
      return this.password.trim() !== '' && this.email.trim() !== ''
    }
  },
  methods: {
    login(): void {
      if (this.loading)
        return

      this.showErrorMessage = false
      this.errorMessage = ''
      this.showEmailConfirmationNeeded = false
      this.loading = true

      const loginMessage = new LoginMessage(this.email, this.password)
      this.miscellaneousService.loginUser(loginMessage).then(tokenResponse => {
        this.loading = false
        const baseRoute = tokenResponse.isEmployee ? '/dashboard' : '/my-quotes'
        router.push(this.returnUrl != '' ? this.returnUrl : baseRoute)
      }).catch(error => {
        const errorsResponse = error as ErrorsResponse

        this.loading = false

        if (errorsResponse.errors.some((e) => e.id === 31004)) {
          this.showEmailConfirmationNeeded = true
        } else if (errorsResponse.errors.some((e) => e.id === 31000 || e.id === 31005)) {
          this.errorMessage = 'Invalid email or password.'
          this.showErrorMessage = true
        } else {
          this.errorMessage = errorsResponse.errors.map(e => e.message).join("\r\n");
          this.showErrorMessage = true
        }
      })
    },
    onEnterPressed(): void {
      if (this.formValid)
        this.login()
    }
  }
})
</script>

<style scoped lang="scss">

.login-view {
  @include sac-floating-card-view;
}

</style>
